import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, PlugZap, Power, RefreshCw, Save, ShieldAlert } from 'lucide-react';
import { useState, type ClipboardEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import {
  adresEksportuHr,
  fetchUstawieniaHr,
  KATEGORIE_PODGLADU,
  KOLEJNOSCI_NAZWY,
  podgladHr,
  przepuscHr,
  rozbierzAdresFeedu,
  rozlozZrodlo,
  sprawdzHr,
  synchronizujHr,
  wlaczHr,
  wylaczHr,
  zapiszUstawieniaHr,
  zlozZrodlo,
  type KategoriaPodgladu,
  type KolejnoscNazwy,
  type PodgladHr,
  type SprawdzenieHr,
  type UstawieniaHr,
} from '@/lib/hrplus-api';
import { fetchEmployeeGroups } from '@/lib/employees-api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  HR+ — POŁĄCZENIE Z KADRAMI KLIENTA (etap 1, 06.10.2026).
 *
 *  ⚠️⚠️ DOPÓKI INTEGRACJA JEST WYŁĄCZONA, EKRAN NICZEGO NIE ZMIENIA NA AUTOMATACH: zapisuje
 *  ustawienia, pyta HR+ i pokazuje, co by się stało. Po włączeniu (etap 2) HR+ prowadzi pracowników
 *  wyspy — dodaje, nadpisuje i blokuje — i to musi być powiedziane wprost PRZED włączeniem i po nim
 *  (zamówione: „stosowna informacja, że jak coś dodasz z ręki, to potem i tak zostanie nadpisane").
 *
 *  ⚠️ KLUCZA NIE POKAZUJEMY NIGDY: serwer oddaje sam jego początek. Pole klucza jest zawsze puste
 *  i puste przy zapisie znaczy „zostaw wpisany".
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function HrPlus() {
  const { groupId } = useIslands();
  const qc = useQueryClient();
  const ust = useQuery({
    queryKey: ['hrplus', 'ustawienia', groupId],
    queryFn: () => fetchUstawieniaHr(groupId!),
    enabled: !!groupId,
    /** Przy włączonej integracji przebiegi idą w tle (zegar co 2 min) — stan ma się odświeżać sam. */
    refetchInterval: (q) => (q.state.data?.wlaczona ? 10_000 : false),
  });
  if (!groupId || !ust.data) return null;
  const odswiez = () => void qc.invalidateQueries({ queryKey: ['hrplus', 'ustawienia', groupId] });

  return (
    <div className="space-y-4">
      <StanHr groupId={groupId} ust={ust.data} onZmiana={odswiez} />
      <PolaczenieHr key={groupId} groupId={groupId} ust={ust.data} onZmiana={odswiez} />
      {ust.data.skonfigurowana && ust.data.kluczUstawiony && <PodgladPanel key={`p-${groupId}`} groupId={groupId} onZmiana={odswiez} />}
    </div>
  );
}

function StanHr({ groupId, ust, onZmiana }: { groupId: string; ust: UstawieniaHr; onZmiana: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [pracuje, setPracuje] = useState(false);
  const [pytanie, setPytanie] = useState(false);
  const [zgoda, setZgoda] = useState(false);
  const kiedy = (iso: string) => new Date(iso).toLocaleString('pl-PL');
  const gotowa = ust.skonfigurowana && ust.kluczUstawiony && !ust.kluczNieczytelny;

  const akcja = async (wywolanie: () => Promise<unknown>, komunikat: string) => {
    setPracuje(true);
    try {
      await wywolanie();
      toast.success(komunikat);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setPracuje(false);
      setPytanie(false);
      setZgoda(false);
      onZmiana();
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {ust.wlaczona ? (
          <Badge variant="success">{t('hr.stan.wlaczona')}</Badge>
        ) : (
          <Badge variant="neutral">{t('hr.stan.podglad')}</Badge>
        )}
        {ust.wlaczona && ust.wlaczonaO && (
          <span className="text-xs text-muted-foreground">{t('hr.wlaczonaOd').replace('{kiedy}', kiedy(ust.wlaczonaO))}</span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {/*
            ⚠️ ZWYKŁY ODNOŚNIK, nie `fetch` — jak kopia portalu. Plik startowy do zaczytania w HR+
            zamiast wczytywania ludzi przez API (zamówione: „nie chcemy robić tego kroku po api").
          */}
          <Button asChild size="sm" variant="outline">
            <a href={adresEksportuHr(groupId)}>
              <Download />
              {t('hr.eksport')}
            </a>
          </Button>
          {ust.wlaczona ? (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={pracuje}
                onClick={() => void akcja(() => synchronizujHr(groupId), t('hr.zsynchronizowano'))}
              >
                {pracuje ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                {t('hr.synchronizuj')}
              </Button>
              <Button size="sm" variant="outline" disabled={pracuje} onClick={() => void akcja(() => wylaczHr(groupId), t('hr.wylaczono'))}>
                <Power />
                {t('hr.wylacz')}
              </Button>
            </>
          ) : (
            gotowa && (
              <Button size="sm" disabled={pracuje} onClick={() => setPytanie(true)}>
                <Power />
                {t('hr.wlacz')}
              </Button>
            )
          )}
        </div>
      </div>
      {ust.wlaczona && <p className="text-xs text-[var(--warning)]">{t('hr.nadpisze')}</p>}
      {ust.wstrzymanaBlokad != null && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2 text-xs">
          <ShieldAlert className="size-4 shrink-0" />
          <div className="space-y-0.5">
            <p className="font-medium">{opisWstrzymania(t, ust)}</p>
            <p className="text-muted-foreground">
              {[
                ust.wstrzymanaPowod ? powodWstrzymania(t, ust) : null,
                ust.wstrzymanaO ? t('hr.czekaOd').replace('{kiedy}', kiedy(ust.wstrzymanaO)) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <Button size="sm" className="ml-auto" disabled={pracuje} onClick={() => setZgoda(true)}>
            {t('hr.przepusc')}
          </Button>
        </div>
      )}
      {/*
        ⚠️ ZNACZNIKI W LICZBACH (12.10.2026): ilu ludzi wyspy HR+ już prowadzi, a ilu spoza niego —
        to są ci, których obejmie blokowanie osób spoza HR+. Lista po nazwisku: filtr „HR+" u pracowników.
      */}
      {ust.liczby && ust.skonfigurowana && (
        <p className="text-xs">
          {t('hr.liczby')
            .replace('{z}', String(ust.liczby.zHr))
            .replace('{zb}', String(ust.liczby.zHrZablokowani))
            .replace('{s}', String(ust.liczby.spoza))
            .replace('{sa}', String(ust.liczby.spozaAktywni))
            .replace('{w}', String(ust.liczby.wyjatki))
            .replace('{sz}', String(ust.liczby.spozaZablokowani))}
        </p>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {ust.ostatniPrzebiegO && ust.ostatniPrzebieg && (
          <span>{t('hr.ostatniPrzebieg').replace('{kiedy}', kiedy(ust.ostatniPrzebiegO)).replace('{opis}', ust.ostatniPrzebieg)}</span>
        )}
        {ust.uzgodnienieO && <span>{t('hr.uzgodnienie').replace('{kiedy}', kiedy(ust.uzgodnienieO))}</span>}
        {!ust.wlaczona && ust.sprawdzonoO && <span>{t('hr.sprawdzono').replace('{kiedy}', kiedy(ust.sprawdzonoO))}</span>}
      </div>
      {ust.ostatniBlad && <p className="text-xs text-[var(--destructive)]">{ust.ostatniBlad}</p>}

      {/*
        ⚠️ ZGODA PRZEZ OKNO Z LISTĄ: „Zezwól" odcina ludzi od automatów — człowiek ma zobaczyć kogo,
        zanim kliknie. Lista z serwera to pierwsze 50; reszta jako liczba.
      */}
      {zgoda && ust.wstrzymanaBlokad != null && (
        <Dialog open onOpenChange={(v) => !v && setZgoda(false)}>
          <DialogContent title={t('hr.zgodaTytul')} description={opisWstrzymania(t, ust)}>
            {ust.wstrzymanaPowod && <p className="text-sm">{powodWstrzymania(t, ust)}</p>}
            {ust.wstrzymanaKto.length > 0 && (
              <div className="max-h-72 overflow-auto rounded-md border border-border">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs text-muted-foreground">
                      <th className="px-3 py-1.5 font-normal">{t('hr.kol.numer')}</th>
                      <th className="px-3 py-1.5 font-normal">{t('hr.kol.osoba')}</th>
                      <th className="px-3 py-1.5 font-normal">{t('hr.kol.jak')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ust.wstrzymanaKto.map((o) => (
                      <tr key={o.numer} className="border-t border-border/50">
                        <td className="whitespace-nowrap px-3 py-1 font-mono text-xs">{o.numer}</td>
                        <td className="px-3 py-1">{o.nazwa || '—'}</td>
                        <td className="px-3 py-1 text-xs text-muted-foreground">{t(`hr.jak.${o.jak}`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {ust.wstrzymanaBlokad > ust.wstrzymanaKto.length && (
                  <p className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
                    {t('hr.pierwsze').replace('{n}', String(ust.wstrzymanaKto.length)).replace('{z}', String(ust.wstrzymanaBlokad))}
                  </p>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setZgoda(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={pracuje}
                onClick={() => void akcja(() => przepuscHr(groupId), t('hr.przepuszczono'))}
              >
                {pracuje && <Loader2 className="animate-spin" />}
                {t('hr.przepusc')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {pytanie && (
        <Dialog open onOpenChange={(v) => !v && setPytanie(false)}>
          <DialogContent title={t('hr.wlaczTytul')} description={t('hr.wlaczOpis')}>
            <p className="text-sm">{t('hr.wlaczBezpiecznik').replace('{p}', String(ust.progBlokad))}</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPytanie(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="button" disabled={pracuje} onClick={() => void akcja(() => wlaczHr(groupId), t('hr.wlaczono'))}>
                {pracuje && <Loader2 className="animate-spin" />}
                {t('hr.wlacz')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/**
 * Belka i okno zgody: ilu ludzi i z ilu (podstawa procentu). Bez podstawy (wiersz sprzed 09.10.2026) —
 * liczba i próg, jak dotąd.
 */
function opisWstrzymania(t: (k: string) => string, ust: UstawieniaHr): string {
  const n = ust.wstrzymanaBlokad ?? 0;
  if (ust.wstrzymanaZ == null || ust.wstrzymanaZ <= 0) {
    return t('hr.wstrzymane').replace('{n}', String(n)).replace('{p}', String(ust.progBlokad));
  }
  return t('hr.wstrzymaneZ')
    .replace('{n}', String(n))
    .replace('{z}', String(ust.wstrzymanaZ))
    .replace('{pct}', String(Math.round((n * 100) / ust.wstrzymanaZ)));
}

function powodWstrzymania(t: (k: string) => string, ust: UstawieniaHr): string {
  return t(`hr.powod.${ust.wstrzymanaPowod}`).replace('{p}', String(ust.progBlokad)).replace('{pct}', String(ust.progProcent));
}

function PolaczenieHr({ groupId, ust, onZmiana }: { groupId: string; ust: UstawieniaHr; onZmiana: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const zrodlo = rozlozZrodlo(ust.zrodloKarty);
  const [adres, setAdres] = useState(ust.adres ?? '');
  const [serwisId, setSerwisId] = useState(ust.serwisId ?? '');
  const [klucz, setKlucz] = useState('');
  const [rodzaj, setRodzaj] = useState(zrodlo.rodzaj);
  const [atrybut, setAtrybut] = useState(zrodlo.atrybut);
  const [kolejnosc, setKolejnosc] = useState<KolejnoscNazwy>(ust.kolejnoscNazwy);
  const [zakladajMpk, setZakladajMpk] = useState(ust.zakladajMpk);
  const [progBlokad, setProgBlokad] = useState(String(ust.progBlokad));
  const [blokujSpoza, setBlokujSpoza] = useState(ust.blokujSpoza);
  const [wyjatkiGrup, setWyjatkiGrup] = useState<string[]>(ust.wyjatkiGrup);
  /** Grupy wyspy — do wyjątków od „Blokuj osoby spoza HR+". */
  const grupy = useQuery({ queryKey: ['employees', 'groups', groupId], queryFn: () => fetchEmployeeGroups(groupId) });
  const [zapisuje, setZapisuje] = useState(false);
  const [sprawdza, setSprawdza] = useState(false);
  const [sprawdzenie, setSprawdzenie] = useState<SprawdzenieHr | null>(null);

  const blad = (e: unknown) => toast.error(e instanceof ApiError ? e.message : t('common.error'));

  /**
   * Adres feedu skopiowany z HR+ — w którekolwiek z dwóch pól — rozkłada się na oba.
   *
   * ⚠️ PRZY WKLEJENIU I PO WYJŚCIU Z POLA, NIE PRZY KAŻDYM ZNAKU (zmierzone w Chrome 13.10): przy
   * pisaniu adres jest „adresem feedu" już od pierwszego znaku serwisu (`…/api/feed/7`), więc pole
   * rozkładało się w pół słowa, a reszta adresu lądowała w polu serwisu.
   */
  const rozbierz = (tekst: string): boolean => {
    const feed = rozbierzAdresFeedu(tekst);
    if (feed === null) return false;
    setAdres(feed.adres);
    setSerwisId(feed.serwisId);
    return true;
  };
  const wklejono = (e: ClipboardEvent<HTMLInputElement>) => {
    if (rozbierz(e.clipboardData.getData('text'))) e.preventDefault();
  };

  const zapisz = async () => {
    setZapisuje(true);
    try {
      const po = await zapiszUstawieniaHr({
        groupId,
        adres,
        serwisId,
        ...(klucz.trim() !== '' ? { klucz: klucz.trim() } : {}),
        zrodloKarty: zlozZrodlo(rodzaj, atrybut),
        kolejnoscNazwy: kolejnosc,
        zakladajMpk,
        progBlokad: Number(progBlokad),
        blokujSpoza,
        wyjatkiGrup,
      });
      setKlucz('');
      /** Inny adres albo serwis wyłącza integrację (serwer) — to nie może przejść jako zwykłe „Zapisano". */
      if (ust.wlaczona && !po.wlaczona) toast.error(t('hr.wylaczonaPoZmianie'));
      else toast.success(t('hr.zapisano'));
      onZmiana();
    } catch (e) {
      blad(e);
    } finally {
      setZapisuje(false);
    }
  };

  const sprawdz = async () => {
    setSprawdza(true);
    setSprawdzenie(null);
    try {
      setSprawdzenie(await sprawdzHr(groupId));
    } catch (e) {
      blad(e);
    } finally {
      setSprawdza(false);
      onZmiana();
    }
  };

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h2 className="text-sm font-medium">{t('hr.polaczenie')}</h2>
      {!ust.sejf && <p className="text-xs text-[var(--destructive)]">{t('hr.brakSejfu')}</p>}
      {ust.kluczNieczytelny && <p className="text-xs text-[var(--destructive)]">{t('hr.kluczNieczytelny')}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('hr.adres')}
          <Input
            value={adres}
            placeholder="https://hr.firma.ergoflow.app"
            onChange={(e) => setAdres(e.target.value)}
            onPaste={wklejono}
            onBlur={(e) => rozbierz(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('hr.serwis')}
          <Input
            value={serwisId}
            className="font-mono"
            placeholder={t('hr.serwisPodpowiedz')}
            onChange={(e) => setSerwisId(e.target.value)}
            onPaste={wklejono}
            onBlur={(e) => rozbierz(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('hr.klucz')}
          <Input
            type="password"
            autoComplete="off"
            value={klucz}
            placeholder={ust.kluczPoczatek ? t('hr.kluczJest').replace('{p}', ust.kluczPoczatek) : 'hrpf_…'}
            onChange={(e) => setKlucz(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('hr.kolejnosc')}
          <Select value={kolejnosc} onChange={(e) => setKolejnosc(e.target.value as KolejnoscNazwy)}>
            {KOLEJNOSCI_NAZWY.map((k) => (
              <option key={k} value={k}>
                {t(`hr.kolejnosc.${k}`)}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('hr.zrodlo')}
          <Select value={rodzaj} onChange={(e) => setRodzaj(e.target.value as typeof rodzaj)}>
            <option value="employeeNo">{t('hr.zrodlo.employeeNo')}</option>
            <option value="atrybut">{t('hr.zrodlo.atrybut')}</option>
            <option value="inne">{t('hr.zrodlo.inne')}</option>
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('hr.progBlokad')}
          <Input type="number" min={1} value={progBlokad} className="w-32" onChange={(e) => setProgBlokad(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <input
            type="checkbox"
            className="size-3.5 accent-[var(--primary)]"
            checked={zakladajMpk}
            onChange={(e) => setZakladajMpk(e.target.checked)}
          />
          {t('hr.zakladajMpk')}
        </label>
        {rodzaj !== 'employeeNo' && (
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {rodzaj === 'atrybut' ? t('hr.atrybut') : t('hr.sciezka')}
            <Input value={atrybut} className="font-mono" onChange={(e) => setAtrybut(e.target.value)} />
          </label>
        )}
      </div>
      {/*
        ⚠️ „BLOKUJ OSOBY SPOZA HR+" (12.10.2026) — przy pełnej liście z HR+ blokujemy ludzi, których HR+
        nie zna, poza wyjątkami. Wyjątki z grupy tutaj, wyjątek osoby na jej karcie.
      */}
      <div className="space-y-2 rounded-md border border-border p-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-3.5 accent-[var(--primary)]"
            checked={blokujSpoza}
            onChange={(e) => setBlokujSpoza(e.target.checked)}
          />
          {t('hr.blokujSpoza')}
        </label>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{t('hr.wyjatkiGrup')}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {(grupy.data ?? []).map((g) => (
              <label key={g.id} className="flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  className="size-3.5 accent-[var(--primary)]"
                  checked={wyjatkiGrup.includes(g.id.toLowerCase())}
                  onChange={(e) => {
                    const id = g.id.toLowerCase();
                    setWyjatkiGrup((w) => (e.target.checked ? [...w, id] : w.filter((x) => x !== id)));
                  }}
                />
                {g.name}
                <span className="text-muted-foreground">({g._count.members})</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => void zapisz()} disabled={zapisuje}>
          {zapisuje ? <Loader2 className="animate-spin" /> : <Save />}
          {t('hr.zapisz')}
        </Button>
        {ust.skonfigurowana && ust.kluczUstawiony && (
          <Button size="sm" variant="outline" onClick={() => void sprawdz()} disabled={sprawdza}>
            {sprawdza ? <Loader2 className="animate-spin" /> : <PlugZap />}
            {t('hr.sprawdz')}
          </Button>
        )}
      </div>
      {sprawdzenie && (
        <div className="space-y-1 rounded-md border border-border bg-muted/40 p-3 text-xs">
          <p className="font-medium">{t('hr.wHr').replace('{n}', String(sprawdzenie.total))}</p>
          <p className="text-muted-foreground">
            {sprawdzenie.atrybuty.length > 0
              ? t('hr.polaDodatkowe').replace('{a}', sprawdzenie.atrybuty.join(', '))
              : t('hr.brakPolDodatkowych')}
          </p>
          {sprawdzenie.przyklad && (
            <p className="text-muted-foreground">
              {sprawdzenie.przyklad.numerKarty
                ? t('hr.przyklad').replace('{nazwa}', sprawdzenie.przyklad.nazwa).replace('{numer}', sprawdzenie.przyklad.numerKarty)
                : t('hr.przykladBezKarty').replace('{nazwa}', sprawdzenie.przyklad.nazwa)}
              {sprawdzenie.przyklad.bladKarty && <span className="text-[var(--destructive)]"> · {sprawdzenie.przyklad.bladKarty}</span>}
            </p>
          )}
          {/*
            ⚠️ BEZ ZAKŁADKI VENDING synchronizacja idzie trybem z etapu 2 (numer karty ze ścieżki
            z ustawień, bez aliasu, flag, grup i automatów) — to musi być widać przed włączeniem.
          */}
          {sprawdzenie.zBlokiem === true && <p className="text-muted-foreground">{t('hr.zakladkaJest')}</p>}
          {sprawdzenie.zBlokiem === false && <p className="text-[var(--warning)]">{t('hr.zakladkaBrak')}</p>}
        </div>
      )}
    </section>
  );
}

/** Kategorie, które coś blokują — wyróżnione, żeby było widać, co trzeba wyjaśnić z HR+. */
const PROBLEMY: ReadonlySet<KategoriaPodgladu> = new Set(['bezKarty', 'zlyNumer', 'zdublowani']);

function PodgladPanel({ groupId, onZmiana }: { groupId: string; onZmiana: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [dane, setDane] = useState<PodgladHr | null>(null);
  const [laduje, setLaduje] = useState(false);
  const [otwarta, setOtwarta] = useState<KategoriaPodgladu | null>(null);

  const porownaj = async () => {
    setLaduje(true);
    try {
      setDane(await podgladHr(groupId));
      setOtwarta(null);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setLaduje(false);
      onZmiana();
    }
  };

  const ile: Record<KategoriaPodgladu, number> | null = dane && {
    zmiany: dane.dopasowani - dane.bezZmian,
    nowi: dane.nowi,
    przywroceni: dane.przywroceni,
    bezAutomatu: dane.bezAutomatu,
    bezKarty: dane.bezKarty,
    zlyNumer: dane.zlyNumer,
    zdublowani: dane.zdublowani,
    tylkoUNas: dane.tylkoUNas,
  };
  const probki = otwarta && dane ? (dane.probki[otwarta] ?? []) : [];

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-medium">{t('hr.podglad')}</h2>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => void porownaj()} disabled={laduje}>
          {laduje ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          {t('hr.porownaj')}
        </Button>
      </div>

      {dane && ile && (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <span>{t('hr.wHr').replace('{n}', String(dane.wHr))}</span>
            <span>
              {t('hr.dopasowani').replace('{n}', String(dane.dopasowani))}
              <span className="text-muted-foreground"> · {t('hr.bezZmian').replace('{n}', String(dane.bezZmian))}</span>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
            {KATEGORIE_PODGLADU.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setOtwarta((o) => (o === k ? null : k))}
                aria-pressed={otwarta === k}
                className={`rounded-md border p-2 text-left transition-colors hover:bg-accent/50 focus-visible:outline-2 focus-visible:outline-[var(--primary)] ${
                  otwarta === k ? 'border-[var(--primary)]' : 'border-border'
                }`}
              >
                <span
                  className={`block text-lg font-semibold tabular-nums ${PROBLEMY.has(k) && ile[k] > 0 ? 'text-[var(--warning)]' : ''}`}
                >
                  {ile[k]}
                </span>
                <span className="block text-xs text-muted-foreground">{t(`hr.kat.${k}`)}</span>
              </button>
            ))}
          </div>
          {ile.zmiany > 0 && (
            <p className="text-xs text-muted-foreground">
              {[
                t('hr.zmianyNazwy').replace('{n}', String(dane.zmianyNazwy)),
                t('hr.zmianyMpk').replace('{n}', String(dane.zmianyMpk)),
                t('hr.doOdblokowania').replace('{n}', String(dane.doOdblokowania)),
                ...(dane.doZablokowania > 0 ? [t('hr.doZablokowania').replace('{n}', String(dane.doZablokowania))] : []),
                ...(dane.zmianyAliasu > 0 ? [t('hr.zmianyAliasu').replace('{n}', String(dane.zmianyAliasu))] : []),
                ...(dane.zmianyFlag > 0 ? [t('hr.zmianyFlag').replace('{n}', String(dane.zmianyFlag))] : []),
                ...(dane.zmianyGrup > 0 ? [t('hr.zmianyGrup').replace('{n}', String(dane.zmianyGrup))] : []),
                ...(dane.zmianyAutomatow > 0 ? [t('hr.zmianyAutomatow').replace('{n}', String(dane.zmianyAutomatow))] : []),
              ].join(' · ')}
            </p>
          )}
          {(dane.zBlokiem === 0 && dane.bezBloku > 0) && <p className="text-xs text-[var(--warning)]">{t('hr.zakladkaBrak')}</p>}
          {dane.tylkoUNas > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('hr.spozaPodglad')
                .replace('{b}', String(dane.spozaDoBlokady))
                .replace('{w}', String(dane.spozaWyjatki))
                .replace('{o}', String(dane.spozaDoOdblokowania))}
            </p>
          )}
          {(dane.obceIdentyfikatory > 0 || dane.zlyBlok > 0 || (dane.zBlokiem > 0 && dane.bezBloku > 0)) && (
            <ul className="space-y-0.5 text-xs text-[var(--warning)]">
              {dane.obceIdentyfikatory > 0 && <li>{t('hr.obceIdentyfikatory').replace('{n}', String(dane.obceIdentyfikatory))}</li>}
              {dane.zlyBlok > 0 && <li>{t('hr.zlyBlok').replace('{n}', String(dane.zlyBlok))}</li>}
              {dane.zBlokiem > 0 && dane.bezBloku > 0 && <li>{t('hr.bezBloku').replace('{n}', String(dane.bezBloku))}</li>}
            </ul>
          )}
          {(dane.noweMpkIle > 0 || dane.dlugieNazwy > 0 || dane.zaDlugieMpk > 0) && (
            <ul className="space-y-0.5 text-xs text-muted-foreground">
              {dane.noweMpkIle > 0 && (
                <li>
                  {t('hr.noweMpk').replace('{n}', String(dane.noweMpkIle))}: <span className="font-mono">{dane.noweMpk.join(', ')}</span>
                </li>
              )}
              {dane.dlugieNazwy > 0 && <li>{t('hr.dlugieNazwy').replace('{n}', String(dane.dlugieNazwy))}</li>}
              {dane.zaDlugieMpk > 0 && <li>{t('hr.zaDlugieMpk').replace('{n}', String(dane.zaDlugieMpk))}</li>}
            </ul>
          )}
          {otwarta && (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2 font-normal">{t('hr.kol.numer')}</th>
                    <th className="px-3 py-2 font-normal">{t('hr.kol.nazwa')}</th>
                    <th className="px-3 py-2 font-normal">{t('hr.kol.uwagi')}</th>
                  </tr>
                </thead>
                <tbody>
                  {probki.map((p, i) => (
                    <tr key={`${p.hrId ?? p.numer ?? ''}-${i}`} className="border-t border-border/50 align-top">
                      <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">{p.numer ?? '—'}</td>
                      <td className="px-3 py-1.5">{p.nazwa || '—'}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{p.powod ?? p.zmiany?.join(' · ') ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {ile[otwarta] > probki.length && (
                <p className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
                  {t('hr.pierwsze').replace('{n}', String(probki.length)).replace('{z}', String(ile[otwarta]))}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
